import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Invoice } from '@/types/models';
import { InvoiceService } from '@/services/invoiceService';
import { supabase } from '@/lib/supabase';
import { formatCurrency } from '@/lib/format';
import { format } from 'date-fns';
import { formatPaymentDate, formatDateInTimezone } from '@/utils/dateUtils';
import { useOrganization } from '@/hooks/useOrganization';
import { FileText, Download, Mail, CreditCard, Printer } from 'lucide-react';

interface ProfessionalInvoiceViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice | null;
  organizationId: string;
}

interface LineItem {
  id: string;
  description: string;
  amount_cents: number;
  class?: { name: string };
  student?: { first_name: string; last_name: string };
}

interface Organization {
  id: string;
  name: string;
  logo_url?: string;
  contact_email?: string;
  contact_phone?: string;
  timezone?: string;
}

interface Payment {
  id: string;
  amount_cents: number;
  payment_method: string;
  paid_at: string;
  status: string;
}

const ProfessionalInvoiceView: React.FC<ProfessionalInvoiceViewProps> = ({
  open,
  onOpenChange,
  invoice,
  organizationId,
}) => {
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      const loadData = async () => {
        // Load organization first to get timezone, then load other data
        await loadOrganization();
        loadInvoiceDetails();
        loadPayments();
      };
      loadData();
    }
  }, [open, invoice]);

  const loadInvoiceDetails = async () => {
    if (!invoice) return;
    
    setLoading(true);
    try {
      const items = await InvoiceService.getInvoiceLineItems(invoice.id);
      setLineItems(items || []);
    } catch (error) {
      console.error('Error loading invoice details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganization = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, logo_url, contact_email, contact_phone, timezone')
        .eq('id', organizationId)
        .single();

      if (error) throw error;
      
      
      setOrganization(data);
    } catch (error) {
      console.error('Error loading organization:', error);
    }
  };

  const loadPayments = async () => {
    if (!invoice) return;
    
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('id, amount_cents, payment_method, paid_at, status')
        .eq('invoice_id', invoice.id)
        .eq('status', 'succeeded')
        .order('paid_at', { ascending: true });

      if (error) throw error;
      setPayments(data || []);
    } catch (error) {
      console.error('Error loading payments:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'unpaid': return 'bg-red-100 text-red-800 border-red-200';
      case 'overdue': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'partial': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const subtotal = lineItems.reduce((sum, item) => sum + item.amount_cents, 0);
  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount_cents, 0);
  const remainingBalance = invoice ? invoice.total_cents - totalPaid : 0;
  
  const formatAmount = (amountInCents: number): string => {
    return formatCurrency(amountInCents / 100);
  };

  const handlePrintSave = () => {
    // Use browser print functionality which allows both printing and saving as PDF
    window.print();
  };

  const handleEmailInvoice = () => {
    if (!invoice || !organization) return;
    
    // Create mailto link with invoice details
    const subject = `Invoice from ${organization.name}`;
    const body = `Please find your invoice details below:\n\nInvoice ID: ${invoice.id.substring(0, 8).toUpperCase()}\nAmount: ${formatAmount(invoice.total_cents)}\nDue Date: ${organization?.timezone ? formatDateInTimezone(invoice.due_date, organization.timezone, 'MMMM d, yyyy') : format(new Date(invoice.due_date), 'MMMM d, yyyy')}\n\nThank you for your business!`;
    
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);
  };

  const handleProcessPayment = () => {
    // TODO: Implement payment processing
    // For now, show coming soon message
    alert('Payment processing integration coming soon! Please contact the organization directly to process payment.');
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 bg-white">
        {/* Professional Header with Logo */}
        <div className="bg-slate-50 border-b border-slate-200 p-8">
          <div className="flex items-start justify-between">
            {/* Organization Logo and Info */}
            <div className="flex items-start space-x-6">
              {organization?.logo_url ? (
                <div className="flex-shrink-0">
                  <img 
                    src={organization.logo_url} 
                    alt={organization.name}
                    className="h-16 w-auto max-w-[200px] object-contain"
                  />
                </div>
              ) : (
                <div className="flex-shrink-0">
                  <div className="h-16 w-16 bg-slate-600 rounded-lg flex items-center justify-center">
                    <FileText className="h-8 w-8 text-white" />
                  </div>
                </div>
              )}
              
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-slate-900">
                  {organization?.name || 'Organization'}
                </h1>
                {organization?.contact_email && (
                  <p className="text-slate-600">{organization.contact_email}</p>
                )}
                {organization?.contact_phone && (
                  <p className="text-slate-600">{organization.contact_phone}</p>
                )}
              </div>
            </div>

            {/* Invoice Status */}
            <div className="text-right">
              <Badge 
                variant="outline" 
                className={`px-4 py-2 text-sm font-medium ${getStatusColor(invoice.status)}`}
              >
                {invoice.status.toUpperCase()}
              </Badge>
            </div>
          </div>
        </div>

        {/* Invoice Details Header */}
        <div className="p-8 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Invoice Information */}
            <div className="space-y-4">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-2">INVOICE</h2>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600 font-medium">Invoice Number:</span>
                    <span className="font-mono text-slate-900">
                      {invoice.invoice_number || 'PENDING'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 font-medium">Issue Date:</span>
                    <span className="text-slate-900">
                      {organization?.timezone ? formatDateInTimezone(invoice.created_at, organization.timezone, 'MMMM d, yyyy') : format(new Date(invoice.created_at), 'MMMM d, yyyy')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 font-medium">Due Date:</span>
                    <span className="text-slate-900 font-semibold">
                      {organization?.timezone ? formatDateInTimezone(invoice.due_date, organization.timezone, 'MMMM d, yyyy') : format(new Date(invoice.due_date), 'MMMM d, yyyy')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bill To */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">BILL TO</h3>
                <div className="bg-slate-50 p-4 rounded-lg border">
                  <p className="font-semibold text-slate-900 text-lg">
                    {invoice.family?.name || 'Family'}
                  </p>
                  <p className="text-slate-600 mt-1">Family Account</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Line Items */}
        <div className="p-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-6">CHARGES & SERVICES</h3>
          
          <div className="overflow-hidden border border-slate-200 rounded-lg">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 border-b border-slate-200">
                    Description
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 border-b border-slate-200">
                    Class
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 border-b border-slate-200">
                    Student
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900 border-b border-slate-200">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                      Loading line items...
                    </td>
                  </tr>
                ) : lineItems.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                      No line items found
                    </td>
                  </tr>
                ) : (
                  lineItems.map((item, index) => (
                    <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-6 py-4 text-slate-900">
                        {item.description}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {item.class?.name || '-'}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {item.student ? `${item.student.first_name} ${item.student.last_name}` : '-'}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-slate-900">
                        {formatAmount(item.amount_cents)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mt-6">
            <div className="w-80">
              <div className="bg-slate-50 p-6 rounded-lg border">
                <div className="space-y-3">
                  {lineItems.length > 0 && (
                    <>
                      <div className="flex justify-between text-slate-600">
                        <span>Subtotal:</span>
                        <span className="font-mono">{formatAmount(subtotal)}</span>
                      </div>
                      <Separator />
                    </>
                  )}
                  <div className="flex justify-between text-lg font-bold text-slate-900">
                    <span>Invoice Total:</span>
                    <span className="font-mono">{formatAmount(invoice.total_cents)}</span>
                  </div>
                  
                  {payments.length > 0 && (
                    <>
                      <Separator />
                      <div className="flex justify-between text-slate-600">
                        <span>Total Paid:</span>
                        <span className="font-mono text-green-700">{formatAmount(totalPaid)}</span>
                      </div>
                      
                      {remainingBalance > 0 && (
                        <div className="flex justify-between text-lg font-bold text-orange-700">
                          <span>Remaining Balance:</span>
                          <span className="font-mono">{formatAmount(remainingBalance)}</span>
                        </div>
                      )}
                      
                      {remainingBalance <= 0 && (
                        <div className="flex justify-between text-lg font-bold text-green-700">
                          <span>Status:</span>
                          <span>PAID IN FULL</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment History */}
          {payments.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">PAYMENT HISTORY</h3>
              <div className="overflow-hidden border border-slate-200 rounded-lg">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 border-b border-slate-200">
                        Payment Date
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 border-b border-slate-200">
                        Method
                      </th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900 border-b border-slate-200">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment, index) => (
                      <tr key={payment.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-6 py-3 text-slate-900">
                          {formatPaymentDate(payment.paid_at, organization?.timezone)}
                        </td>
                        <td className="px-6 py-3 text-slate-600 capitalize">
                          {payment.payment_method}
                        </td>
                        <td className="px-6 py-3 text-right font-mono text-slate-900">
                          {formatAmount(payment.amount_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-slate-50 border-t border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Generated on {organization?.timezone ? formatDateInTimezone(new Date().toISOString(), organization.timezone, 'MMMM d, yyyy \'at\' h:mm a') : format(new Date(), 'MMMM d, yyyy \'at\' h:mm a')}
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" onClick={handlePrintSave}>
                <Printer className="h-4 w-4 mr-2" />
                Print / Save PDF
              </Button>
              <Button variant="outline" size="sm" onClick={handleEmailInvoice}>
                <Mail className="h-4 w-4 mr-2" />
                Email Invoice
              </Button>
              {invoice.status !== 'paid' && remainingBalance > 0 && (
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleProcessPayment}>
                  <CreditCard className="h-4 w-4 mr-2" />
                  {remainingBalance < invoice.total_cents ? `Pay Remaining ${formatAmount(remainingBalance)}` : 'Process Payment'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfessionalInvoiceView;